import { createSlice } from '@reduxjs/toolkit';
import { getWorkingAddress, getVehicleLocation } from './routeApi';

const initialState = {
    startPoint: null,
    endPoint: null,
    viaPoints: [],
    currentlyEditingPointType: null,
    currentlyEditingViaPointIndex: null,
    pointSelectionMethod: null,
    workingZoneId: null,
    workingMarkerId: null,
    workingAddress: null,
    workingLatLng: null,
    workingLocationName: null,
    workingLocationSelectionType: 'zone',
    workingMaxDetentionTime: null,
    workingTriggerRadius: null,
    workingRules: null,
    workingNotifs: null,
    placeRoutePointOnClick: false,
    computeRoute: false,
    routePath: [],
    routeDistance: 0,
    routeTime: 0,
    shouldClearRoute: false,
    routeType: null,
    routeSegments: [],
    currentlyEditingSegmentIndex: null,
    currentWizardStep: 'routeType',
    wizardStepsCompleted: {
        routeType: false,
        locations: false,
        route: false,
        segments: false,
        rules: false,
        notifications: false
    },
    vehicleLocation: null,
    getWorkingAddressLoading: false,
    getWorkingAddressError: null,
    getVehicleLocationLoading: false,
    getVehicleLocationError: null,
    showWorkingAddress: false,
    isMinimized: false,
};

const googleRouteMakerSlice = createSlice({
    name: 'googleRouteMaker',
    initialState,
    reducers: {
        setStartPoint: (state, action) => {
            if (action.payload === null) {
                state.startPoint = null;
                return;
            } else {
                state.startPoint = {
                    lat: action.payload.lat,
                    lng: action.payload.lng,
                    address: action.payload.address,
                    locationName: action.payload.locationName,
                    locationType: action.payload.locationType,
                    zoneCoordinates: action.payload.zoneCoordinates,
                    triggerRadius: action.payload.triggerRadius,
                    maxDetentionTime: action.payload.maxDetentionTime
                };
                return;
            }
        },
        setEndPoint: (state, action) => {
            if (action.payload === null) {
                state.endPoint = null;
                return;
            } else {
                state.endPoint = { lat: action.payload.lat, lng: action.payload.lng, address: action.payload.address, locationName: action.payload.locationName, locationType: action.payload.locationType, zoneCoordinates: action.payload.zoneCoordinates, triggerRadius: action.payload.triggerRadius, maxDetentionTime: action.payload.maxDetentionTime };
                return;
            }
        },
        addViaPoint: (state, action) => {
            state.viaPoints.push({ lat: action.payload.lat, lng: action.payload.lng, address: action.payload.address, locationName: action.payload.locationName, locationType: action.payload.locationType, zoneCoordinates: action.payload.zoneCoordinates, triggerRadius: action.payload.triggerRadius, maxDetentionTime: action.payload.maxDetentionTime });
        },
        removeViaPoint: (state, action) => {
            state.viaPoints.splice(action.payload, 1);
        },
        clearViaPoints: (state) => {
            state.viaPoints = [];
        },
        setCurrentlyEditingPointType: (state, action) => {
            state.currentlyEditingPointType = action.payload;
        },
        setCurrentlyEditingViaPointIndex: (state, action) => {
            state.currentlyEditingViaPointIndex = action.payload;
        },
        setPointSelectionMethod: (state, action) => {
            state.pointSelectionMethod = action.payload;
        },
        setWorkingZoneId: (state, action) => {
            state.workingZoneId = action.payload;
            state.placeRoutePointOnClick = false;
        },
        setWorkingMarkerId: (state, action) => {
            state.workingMarkerId = action.payload;
            state.placeRoutePointOnClick = false;
        },
        setWorkingAddress: (state, action) => {
            if (!action.payload) {
                state.workingAddress = null;
                return;
            }

            if (action.payload.isCustomName) {
                state.workingAddress = action.payload.name;
                return;
            }

            let addr = "";
            if (action.payload.hasOwnProperty('name')) {
                addr = action.payload.name;
            }
            if (action.payload.hasOwnProperty('address_components')) {
                if (action.payload.address_components.length > 1) {
                    for (let i = 1; i < action.payload.address_components.length; i++) {
                        addr += action.payload.address_components[i].long_name;
                        if (i < action.payload.address_components.length - 1) {
                            addr += ", ";
                        }
                    }
                }
            }
            if (addr === "") {
                state.workingAddress = action.payload.addressText;
            } else {
                state.workingAddress = addr;
            }
        },
        setWorkingLatLng: (state, action) => {
            state.workingLatLng = action.payload;
        },
        setWorkingLocationName: (state, action) => {
            state.workingLocationName = action.payload;
        },
        setWorkingLocationSelectionType: (state, action) => {
            state.workingLocationSelectionType = action.payload;
        },
        setWorkingMaxDetentionTime: (state, action) => {
            state.workingMaxDetentionTime = action.payload;
        },
        setWorkingTriggerRadius: (state, action) => {
            state.workingTriggerRadius = action.payload;
        },
        setWorkingRules: (state, action) => {
            state.workingRules = action.payload;
        },
        setWorkingNotifs: (state, action) => {
            state.workingNotifs = action.payload;
        },
        setPlaceRoutePointOnClick: (state, action) => {
            state.placeRoutePointOnClick = action.payload;
            if (action.payload === true) {
                state.showWorkingAddress = true;
            }
        },
        setComputeRoute: (state, action) => {
            state.computeRoute = action.payload;
        },
        routeBuilt: (state, action) => {
            const { path, distance, time } = action.payload;
            state.routeDistance = distance;
            state.routeTime = time;
            state.routePath = path;
        },
        resetRouteDetails: (state) => {
            return initialState;
        },
        routeCleared: (state) => {
            state.shouldClearRoute = false;
        },
        setRouteType: (state, action) => {
            state.routeType = action.payload;
            if (action.payload === 'oneWayDirect' || action.payload === 'roundTrip') {
                state.viaPoints = [];
            }
            state.wizardStepsCompleted.routeType = !!action.payload;
        },
        addRouteSegment: (state, action) => {
            state.routeSegments.push(action.payload);
        },
        updateRouteSegment: (state, action) => {
            const { index, segment } = action.payload;
            if (index >= 0 && index < state.routeSegments.length) {
                state.routeSegments[index] = segment;
            }
        },
        removeRouteSegment: (state, action) => {
            state.routeSegments.splice(action.payload, 1);
        },
        clearRouteSegments: (state) => {
            state.routeSegments = [];
        },
        setCurrentlyEditingSegmentIndex: (state, action) => {
            state.currentlyEditingSegmentIndex = action.payload;
        },
        generateRouteSegments: (state) => {
            const { startPoint, endPoint, viaPoints, routePath } = state;

            // Create ordered array of all points
            const allPoints = [startPoint, ...viaPoints, endPoint];

            // Generate segments
            const segments = [];
            for (let i = 0; i < allPoints.length - 1; i++) {
                const from = allPoints[i];
                const to = allPoints[i + 1];

                // Find route segment between these points
                const segmentPath = findPathSegment(routePath, from, to);

                // Calculate actual segment distance
                const segmentDistance = calculateSegmentDistance(segmentPath);

                segments.push({
                    from: from,
                    to: to,
                    name: `${from.locationName || 'Unknown'} to ${to.locationName || 'Unknown'}`,
                    distance: segmentDistance, // in meters
                    loadType: 'loaded', // default value
                    path: segmentPath // store segment-specific path
                });
            }

            state.routeSegments = segments;
        },
        setCurrentWizardStep: (state, action) => {
            state.currentWizardStep = action.payload;
        },
        setWizardStepCompleted: (state, action) => {
            const { step, completed } = action.payload;
            if (state.wizardStepsCompleted.hasOwnProperty(step)) {
                state.wizardStepsCompleted[step] = completed;
            }
        },
        checkLocationsStepCompleted: (state) => {
            let isComplete = false;

            switch (state.routeType) {
                case 'oneWayDirect':
                    isComplete = !!state.startPoint && !!state.endPoint;
                    break;
                case 'roundTrip':
                    isComplete = !!state.startPoint && !!state.endPoint;
                    break;
                case 'oneWayWithStops':
                    isComplete = !!state.startPoint && !!state.endPoint && state.viaPoints.length > 0;
                    break;
                case 'roundTripWithStops':
                    isComplete = !!state.startPoint && !!state.endPoint && state.viaPoints.length > 0;
                    break;
                default:
                    isComplete = false;
            }

            state.wizardStepsCompleted.locations = isComplete;
        },
        checkRouteStepCompleted: (state) => {
            state.wizardStepsCompleted.route = state.routePath.length > 0;
        },
        checkSegmentsStepCompleted: (state) => {
            if (state.routeSegments.length === 0) {
                state.wizardStepsCompleted.segments = false;
                return;
            }

            const allComplete = state.routeSegments.every(segment =>
                segment.name && segment.loadType
            );

            state.wizardStepsCompleted.segments = allComplete;
        },
        checkRulesStepCompleted: (state) => {
            state.wizardStepsCompleted.rules = !!state.workingRules;
        },
        checkNotificationsStepCompleted: (state) => {
            state.wizardStepsCompleted.notifications = !!state.workingNotifs;
        },
        setShowWorkingAddress: (state, action) => {
            state.showWorkingAddress = action.payload;
        },
        setIsMinimized: (state, action) => {
            state.isMinimized = action.payload;
        },
    },
    extraReducers: {
        [getWorkingAddress.fulfilled]: (state, action) => {
            if (!state.workingAddress || (!state.workingZoneId && !state.workingMarkerId)) {
                state.workingAddress = action.payload.address.Location;
            }   
            state.getWorkingAddressLoading = false;
            state.showWorkingAddress = true;
        },
        [getWorkingAddress.rejected]: (state, action) => {
            state.workingAddress = null;
            state.getWorkingAddressLoading = false;
        },
        [getWorkingAddress.pending]: (state, action) => {
            state.getWorkingAddressLoading = true;
        },
        [getVehicleLocation.fulfilled]: (state, action) => {
            const vehicleLocation = action.payload.vehicleLocation;

            // Store the vehicle location data
            state.vehicleLocation = vehicleLocation;

            // Update working state for the map and UI
            state.workingLatLng = {
                lat: vehicleLocation.lat,
                lng: vehicleLocation.lng
            };

            // Update address information
            state.workingAddress = vehicleLocation.address.locn || vehicleLocation.address.fmtd_addr;

            // Set selection type to point
            state.workingLocationSelectionType = 'point';

            // Set loading state
            state.getVehicleLocationLoading = false;

            // Keep the working address visible
            state.showWorkingAddress = true;
        },
        [getVehicleLocation.rejected]: (state, action) => {
            state.vehicleLocation = null;
            state.getVehicleLocationLoading = false;
        },
        [getVehicleLocation.pending]: (state, action) => {
            state.getVehicleLocationLoading = true;
        },
    },
});

export const {
    setStartPoint,
    setEndPoint,
    addViaPoint,
    removeViaPoint,
    clearViaPoints,
    setCurrentlyEditingPointType,
    setCurrentlyEditingViaPointIndex,
    setPointSelectionMethod,
    setWorkingZoneId,
    setWorkingMarkerId,
    setWorkingAddress,
    setWorkingLatLng,
    setWorkingLocationName,
    setWorkingLocationSelectionType,
    setWorkingMaxDetentionTime,
    setWorkingTriggerRadius,
    setWorkingRules,
    setWorkingNotifs,
    setPlaceRoutePointOnClick,
    setComputeRoute,
    routeBuilt,
    resetRouteDetails,
    routeCleared,
    setRouteType,
    addRouteSegment,
    updateRouteSegment,
    removeRouteSegment,
    clearRouteSegments,
    setCurrentlyEditingSegmentIndex,
    generateRouteSegments,
    setCurrentWizardStep,
    setWizardStepCompleted,
    checkLocationsStepCompleted,
    checkRouteStepCompleted,
    checkSegmentsStepCompleted,
    checkRulesStepCompleted,
    checkNotificationsStepCompleted,
    setShowWorkingAddress,
    setIsMinimized
} = googleRouteMakerSlice.actions;

export { getVehicleLocation, getWorkingAddress };
export const selectStartPoint = (state) => state.googleRouteMaker.startPoint;
export const selectEndPoint = (state) => state.googleRouteMaker.endPoint;
export const selectViaPoints = (state) => state.googleRouteMaker.viaPoints;
export const selectCurrentlyEditingPointType = (state) =>
    state.googleRouteMaker.currentlyEditingPointType;
export const selectCurrentlyEditingViaPointIndex = (state) =>
    state.googleRouteMaker.currentlyEditingViaPointIndex;
export const selectPointSelectionMethod = (state) =>
    state.googleRouteMaker.pointSelectionMethod;
export const selectWorkingZoneId = (state) => state.googleRouteMaker.workingZoneId;
export const selectWorkingMarkerId = (state) => state.googleRouteMaker.workingMarkerId;
export const selectWorkingAddress = (state) => state.googleRouteMaker.workingAddress;
export const selectWorkingLatLng = (state) => state.googleRouteMaker.workingLatLng;
export const selectWorkingLocationName = (state) => state.googleRouteMaker.workingLocationName;
export const selectWorkingLocationSelectionType = (state) => state.googleRouteMaker.workingLocationSelectionType;
export const selectWorkingMaxDetentionTime = (state) => state.googleRouteMaker.workingMaxDetentionTime;
export const selectWorkingTriggerRadius = (state) => state.googleRouteMaker.workingTriggerRadius;
export const selectWorkingRules = (state) => state.googleRouteMaker.workingRules;
export const selectWorkingNotifs = (state) => state.googleRouteMaker.workingNotifs;
export const selectPlaceRoutePointOnClick = (state) => state.googleRouteMaker.placeRoutePointOnClick;
export const selectComputeRoute = (state) => state.googleRouteMaker.computeRoute;
export const selectRoutePath = (state) => state.googleRouteMaker.routePath;
export const selectRouteDistance = (state) => state.googleRouteMaker.routeDistance;
export const selectRouteTime = (state) => state.googleRouteMaker.routeTime;
export const selectShouldClearRoute = (state) => state.googleRouteMaker.shouldClearRoute;

export const selectRouteType = (state) => state.googleRouteMaker.routeType;
export const selectRouteSegments = (state) => state.googleRouteMaker.routeSegments;
export const selectCurrentlyEditingSegmentIndex = (state) => state.googleRouteMaker.currentlyEditingSegmentIndex;
export const selectCurrentWizardStep = (state) => state.googleRouteMaker.currentWizardStep;
export const selectWizardStepsCompleted = (state) => state.googleRouteMaker.wizardStepsCompleted;
export const selectIsWizardStepCompleted = (step) => (state) => state.googleRouteMaker.wizardStepsCompleted[step];

export const selectVehicleLocation = (state) => state.googleRouteMaker.vehicleLocation;
export const selectGetWorkingAddressLoading = (state) => state.googleRouteMaker.getWorkingAddressLoading;
export const selectGetWorkingAddressError = (state) => state.googleRouteMaker.getWorkingAddressError;
export const selectGetVehicleLocationLoading = (state) => state.googleRouteMaker.getVehicleLocationLoading;
export const selectGetVehicleLocationError = (state) => state.googleRouteMaker.getVehicleLocationError;
export const selectShowWorkingAddress = (state) => state.googleRouteMaker.showWorkingAddress;

export const selectIsMinimized = state => state.googleRouteMaker.isMinimized;

export { googleRouteMakerSlice };

/* export const generateRouteSegments = createAsyncThunk(
    'googleRouteMaker/generateRouteSegments',
    async (_, { getState }) => {
        const state = getState().googleRouteMaker;
        const { startPoint, endPoint, viaPoints, routePath } = state;

        // Create ordered array of all points
        const allPoints = [startPoint, ...viaPoints, endPoint];

        // Generate segments
        const segments = [];
        for (let i = 0; i < allPoints.length - 1; i++) {
            const from = allPoints[i];
            const to = allPoints[i + 1];

            // Find route segment between these points
            const segmentPath = findPathSegment(routePath, from, to);

            // Calculate actual segment distance
            const segmentDistance = calculateSegmentDistance(segmentPath);

            segments.push({
                from: from,
                to: to,
                name: `${from.name} to ${to.name}`,
                distance: segmentDistance, // in meters
                loadType: 'loaded', // default value
                path: segmentPath // store segment-specific path
            });
        }

        return segments;
    }
); */

// Helper function to find path segment between two points
const findPathSegment = (fullPath, fromPoint, toPoint) => {
    if (!fullPath || !fromPoint || !toPoint) return [];

    // Find the closest points in the path to our from/to points
    const startIndex = findNearestPointIndexByDistance(fullPath, fromPoint);
    const endIndex = findNearestPointIndexByDistance(fullPath, toPoint, startIndex);

    // If either index is invalid, return empty path
    if (startIndex === -1 || endIndex === -1) return [];

    // Extract segment path, ensuring endIndex is after startIndex
    if (startIndex <= endIndex) {
        return fullPath.slice(startIndex, endIndex + 1);
    } else {
        return fullPath.slice(endIndex, startIndex + 1).reverse();
    }
};

// Helper function to find nearest point index in path using distance calculation
const findNearestPointIndexByDistance = (path, point, startFrom = 0) => {
    if (!path || !point || !path.length) return -1;

    let nearestIndex = -1;
    let minDistance = Infinity;

    // Search through path points to find the nearest one
    for (let i = startFrom; i < path.length; i++) {
        const distance = getDistanceFromLatLonInMeters(
            path[i].lat,
            path[i].lng,
            point.lat,
            point.lng
        );

        if (distance < minDistance) {
            minDistance = distance;
            nearestIndex = i;
        }
    }

    return nearestIndex;
};

// Helper function to calculate segment distance
const calculateSegmentDistance = (pathSegment) => {
    let distance = 0;
    for (let i = 0; i < pathSegment.length - 1; i++) {
        const point1 = pathSegment[i];
        const point2 = pathSegment[i + 1];
        distance += getDistanceFromLatLonInMeters(
            point1.lat,
            point1.lng,
            point2.lat,
            point2.lng
        );
    }
    return distance;
};

// Haversine formula for calculating distance between two points
const getDistanceFromLatLonInMeters = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // Radius of the earth in meters
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in meters
};

const deg2rad = (deg) => {
    return deg * (Math.PI / 180);
};